import { computed, makeAutoObservable, reaction } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { serializeNodeValue, TreeType } from './utils';
import { ToasterState } from 'src/AppState/ToasterState';
import { OpenapiProxyStaffGetCompetitionsResponseType } from 'src/api_openapi/generated/openapi_proxy_staff_get_competitions';

export class CompetitionStoreState {
    private readonly allCompetitionsResource: AutoMap<
        number,
        Resource<OpenapiProxyStaffGetCompetitionsResponseType | null>
    >;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string
    ) {
        this.allCompetitionsResource = new AutoMap(() => {
            return new Resource(() => this.getCompBySport());
        });

        makeAutoObservable(this);

        reaction(
            () => this.getCompetitions,
            (competitions) => {
                if (competitions === 'error') {
                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Error!',
                        message: 'There was an error retrieving competitions.',
                    });
                }
            }
        );
    }

    private async getCompBySport(): Promise<OpenapiProxyStaffGetCompetitionsResponseType> {
        const response = await this.common.trpcClient.client.competitionsRouter.getCompetitions.query({
            data: { sport: this.sportId },
        });

        return response;
    }

    @computed public get getCompetitions(): TreeType[] | 'loading' | 'error' {
        const res = this.allCompetitionsResource.get(parseFloat(this.sportId)).get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error' || res.value === null || res.value.status !== 200) {
            return 'error';
        }

        const fetchedItems = res.value.body;

        return fetchedItems.map((competition) => {
            const serializedValue = serializeNodeValue({
                type: 'competition',
                sportId: this.sportId,
                competitionId: competition.id.toString(),
                name: competition.name,
            });
            return {
                id: serializedValue,
                value: serializedValue,
                label: competition.name,
                name: competition.name,
            };
        });
    }

    @computed public get isLoading(): boolean {
        if (this.getCompetitions === 'loading') {
            return true;
        }

        return false;
    }
}
